import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Check, ChevronDown } from 'lucide-react';
import ScrollReveal from '../components/ScrollReveal';
import { projectsData } from '../projectsData';
import './Projects.css';
import SEO, { seoConfig } from '../components/SEO';

// Menu de filtres pour petit écran. Un <select> natif ne permet pas de styliser
// la liste déroulante (rendue par le système) : on la reconstruit, en gardant
// le clavier et les rôles ARIA d'une vraie liste de choix.
function FilterMenu({ filters, value, onChange, label }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const current = filters.find((f) => f.key === value) || filters[0];

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const pick = (key) => {
    onChange(key);
    setOpen(false);
  };

  return (
    <div className={`filters-menu ${open ? 'is-open' : ''}`} ref={wrapRef}>
      <button
        type="button"
        className="filters-menu-trigger"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={label}
      >
        <span className="filters-menu-value">{current.label}</span>
        <ChevronDown size={18} className="filters-menu-chevron" aria-hidden="true" />
      </button>

      {open && (
        <ul className="filters-menu-list" role="listbox" aria-label={label}>
          {filters.map((filter) => {
            const selected = filter.key === value;
            return (
              <li
                key={filter.key}
                role="option"
                aria-selected={selected}
                tabIndex={0}
                className={`filters-menu-item ${selected ? 'active' : ''}`}
                onClick={() => pick(filter.key)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    pick(filter.key);
                  }
                }}
              >
                {filter.label}
                {selected && <Check size={16} aria-hidden="true" />}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export default function Projects({ t, language }) {
  const location = useLocation();
  
  // Récupérer le filtre depuis l'état de navigation (quand on revient de ProjectDetail)
  const initialFilter = location.state?.filterCategory || 'all';
  const [filterCategory, setFilterCategory] = useState(initialFilter);
  
  const filteredProjects = filterCategory === 'all' 
    ? projectsData 
    : projectsData.filter(p => p.category === filterCategory);
  
  const filters = [
    { key: 'all', label: t.projects.all },
    { key: 'collective', label: t.projects.collective },
    { key: 'individual', label: t.projects.individual },
    { key: 'territory', label: t.projects.territory },
    { key: 'competitions', label: t.projects.competitions },
  ];

  return (
    <div className="projects-page">
      {/* SEO Component */}
      <SEO 
        title={seoConfig.projects.title}
        description={seoConfig.projects.description}
        keywords={seoConfig.projects.keywords}
      />
      
      <div className="page-container">
        <ScrollReveal>
          <h1 className="page-title">{t.projects.title}</h1>
        </ScrollReveal>

        {/* Filtres : barre de boutons sur grand écran, menu déroulant sur mobile.
            Pas de ScrollReveal ici : son transform crée un contexte d'empilement
            qui ferait passer le menu ouvert sous les cartes projets. */}
        <div className="filters-bar">
            <div className="filters" role="group" aria-label={t.projects.title}>
              {filters.map((filter) => (
                <button
                  key={filter.key}
                  onClick={() => setFilterCategory(filter.key)}
                  className={`filter-btn ${filterCategory === filter.key ? 'active' : ''}`}
                  aria-pressed={filterCategory === filter.key}
                >
                  {filter.label}
                </button>
              ))}
            </div>

            <div className="filters-select-wrap">
              <FilterMenu
                filters={filters}
                value={filterCategory}
                onChange={setFilterCategory}
                label={t.projects.title}
              />
            </div>
        </div>

        {/* Projects Grid */}
        <div className="projects-grid">
          {filteredProjects.map((project, idx) => (
            <ScrollReveal key={project.id} delay={idx * 0.05}>
              <Link 
                to={`/project/${project.id}`}
                state={{ filterCategory }} // Passer le filtre actuel
                className="project-card"
              >
                <div className="project-image-wrapper">
                  <img
                    src={project.image}
                    alt={`${project.title} - ${project.location} | Projet architecture Montpellier`}
                    className="project-image"
                    /* les 3 premières vignettes sont au-dessus de la ligne de flottaison :
                       les différer retarderait le LCP. Les suivantes se chargent au scroll. */
                    loading={idx < 3 ? 'eager' : 'lazy'}
                    decoding="async"
                  />
                  <div className="project-overlay">
                    <div className="project-description">
                      <p>{t.projectDescriptions[project.id]}</p>
                    </div>
                  </div>
                </div>
                <h3 className="project-title">{project.title}</h3>
                <p className="project-location">{project.location}</p>
              </Link>
            </ScrollReveal>
          ))}
        </div>
      </div>
    </div>
  );
}